import { useLiveQuery } from "dexie-react-hooks";
import {
  AlertTriangle, Check, ChevronDown, ChevronUp,
  Database, Download, FlaskConical, Globe, Pencil, Plus, RotateCcw, Trash2, X,
} from "lucide-react";
import React, { useState } from "react";
import {
  BackupPanel,
  EmojiPicker,
  generateId,
  LOCALE_CONFIGS,
  type SupportedLocale,
} from "@repo/core";
import { AppCard, SectionTitle } from "../components/ui/AppCard";
import { ThemePresets } from "../components/ThemePresets";
import { CURRENCY_OPTIONS, useCurrency, type CurrencyCode } from "../hooks/useCurrency";
import {
  accountTypeLabel,
  categoryLabel,
  groupLabel,
} from "../i18n/labels";
import { LAUNCH_LOCALES } from "../i18n/localeMeta";
import { useT } from "../i18n/useT";
import { db, clearAllData, resetAndSeed, restoreDefaultCategories } from "../lib/db";
import { handleFormSubmit, onEnterToSubmitKeyDown } from "../lib/formKeyboard";
import { exportAnnualReportFromDb } from "../lib/pdf/exportFromDb";
import {
  runBudgetCalculationTests,
  runCustomerVerification,
  type TestReport,
} from "../lib/testRunner";
import type { Account, AccountType, Category, CategoryGroup } from "../lib/schema";

const LOCALES: SupportedLocale[] = [...LAUNCH_LOCALES];

// ── helpers ───────────────────────────────────────────
const uid = () => generateId();

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">
      {children}
    </label>
  );
}

const GROUP_OPTIONS: CategoryGroup[] = ["income", "needs", "wants", "savings"];
const TYPE_FROM_GROUP = (g: CategoryGroup) => (g === "income" ? "income" : "expense") as Category["type"];

const ACC_TYPES: AccountType[] = ["checking", "savings", "asset", "debt"];

type AccFormState = { name: string; type: AccountType; initialBalance: string };

function AccFormRow({
  form,
  onChange,
  onSave,
  onCancel,
  isEdit,
}: {
  form: AccFormState;
  onChange: (f: AccFormState) => void;
  onSave: () => void;
  onCancel: () => void;
  isEdit?: boolean;
}) {
  const { t } = useT();
  const set = (k: keyof AccFormState, v: string) =>
    onChange({ ...form, [k]: v } as AccFormState);
  return (
    <form
      className="bg-muted/30 border-b border-border px-3 py-3"
      onKeyDown={onEnterToSubmitKeyDown}
      onSubmit={(e) => handleFormSubmit(e, onSave)}
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
        <div className="sm:col-span-2">
          <Label>{t("settings.accountNameLabel")}</Label>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className="app-input w-full"
            placeholder={t("settings.accountName")}
          />
        </div>
        <div>
          <Label>{t("settings.openingBalance")}</Label>
          <input
            type="number"
            step="0.01"
            value={form.initialBalance}
            onChange={(e) => set("initialBalance", e.target.value)}
            className="app-input w-full"
          />
        </div>
        <div>
          <Label>{t("settings.type")}</Label>
          <select
            value={form.type}
            onChange={(e) => set("type", e.target.value as AccountType)}
            className="app-input w-full"
          >
            {ACC_TYPES.map((ty) => (
              <option key={ty} value={ty}>
                {accountTypeLabel(ty, t)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={!form.name} className="app-btn text-xs py-1.5 px-3">
          <Check size={12} />
          {isEdit ? t("common.save") : t("common.add")}
        </button>
        <button type="button" onClick={onCancel} className="app-btn-outline text-xs py-1.5 px-3">
          <X size={12} />
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}

function LanguageSection() {
  const { t, locale, setLocale, i18n } = useT();
  const now = new Date();
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label>
          <span className="inline-flex items-center gap-1.5">
            <Globe size={12} />
            {t("settings.language")}
          </span>
        </Label>
        <select
          value={locale}
          onChange={(e) => void setLocale(e.target.value as SupportedLocale)}
          className="app-input w-full max-w-xs"
        >
          {LOCALES.map((l) => (
            <option key={l} value={l}>
              {LOCALE_CONFIGS[l].label}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <p className="text-muted-foreground font-medium">{t("settings.datePreview")}</p>
          <p className="font-semibold">{i18n.formatDate(now)}</p>
        </div>
        <div>
          <p className="text-muted-foreground font-medium">{t("settings.timePreview")}</p>
          <p className="font-semibold">{i18n.formatTime(now)}</p>
        </div>
        <div>
          <p className="text-muted-foreground font-medium">{t("settings.currencyPreview")}</p>
          <p className="font-semibold">{i18n.formatCurrency(1234.56)}</p>
        </div>
        <div>
          <p className="text-muted-foreground font-medium">{t("settings.relativePreview")}</p>
          <p className="font-semibold">
            {i18n.formatRelative(new Date(Date.now() - 3600_000 * 2))}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Categories section ────────────────────────────────
function CategoriesSection() {
  const { t, locale } = useT();
  const categories = useLiveQuery(() => db.categories.toArray(), []) ?? [];
  const [editing, setEditing] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  type CatForm = { name: string; icon: string; group: CategoryGroup; monthlyTarget: string };
  const EMPTY_CAT: CatForm = { name: "", icon: "📦", group: "needs", monthlyTarget: "" };
  const [catForm, setCatForm] = useState<CatForm>(EMPTY_CAT);

  const saveCategory = async (existing?: Category) => {
    if (!catForm.name || !catForm.monthlyTarget) return;
    const payload: Category = {
      id:            existing?.id ?? uid(),
      name:          catForm.name,
      icon:          catForm.icon,
      group:         catForm.group,
      type:          TYPE_FROM_GROUP(catForm.group),
      monthlyTarget: parseFloat(catForm.monthlyTarget),
    };
    if (existing) {
      await db.categories.put(payload);
      setEditing(null);
    } else {
      await db.categories.add(payload);
      setCatForm(EMPTY_CAT);
      setAddOpen(false);
    }
  };

  const deleteCategory = async (id: string) => {
    if (!confirm(t("settings.deleteCategory"))) return;
    await db.categories.delete(id);
  };

  const startEdit = (cat: Category) => {
    setEditing(cat.id);
    setCatForm({
      name: categoryLabel(cat, t),
      icon: cat.icon ?? "📦",
      group: cat.group,
      monthlyTarget: String(cat.monthlyTarget),
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold">{t("settings.categories")}</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={async () => {
              if (!confirm(t("settings.restoreDefaultsConfirm"))) return;
              await restoreDefaultCategories(locale);
            }}
            className="app-btn-outline text-xs py-1.5 px-2.5"
          >
            {t("settings.restoreDefaults")}
          </button>
          <button onClick={() => setAddOpen((v) => !v)} className="app-btn text-xs py-1.5 px-2.5">
            <Plus size={12} /> {t("common.add")}
          </button>
        </div>
      </div>

      {/* Add form */}
      {addOpen && (
        <CatFormRow
          form={catForm}
          onChange={setCatForm}
          onSave={() => saveCategory()}
          onCancel={() => { setAddOpen(false); setCatForm(EMPTY_CAT); }}
        />
      )}

      {/* List */}
      <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
        {categories.length === 0 && (
          <p className="text-xs text-muted-foreground px-4 py-3">{t("settings.noCategories")}</p>
        )}
        {categories.map((cat) => (
          <div key={cat.id}>
            {editing === cat.id ? (
              <CatFormRow
                form={catForm}
                onChange={setCatForm}
                onSave={() => saveCategory(cat)}
                onCancel={() => setEditing(null)}
                isEdit
              />
            ) : (
              <div className="flex items-center gap-3 px-3 py-2.5 bg-card hover:bg-muted/40 transition-colors">
                <span className="text-lg w-6 text-center">{cat.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{categoryLabel(cat, t)}</p>
                  <p className="text-[10px] text-muted-foreground capitalize">{groupLabel(cat.group, t)}</p>
                </div>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {t("settings.perMonth", { amount: `$${cat.monthlyTarget}` })}
                </p>
                <button onClick={() => startEdit(cat)} className="p-1 rounded hover:bg-accent transition-colors">
                  <Pencil size={12} className="text-muted-foreground" />
                </button>
                <button onClick={() => deleteCategory(cat.id)} className="p-1 rounded hover:bg-destructive/10 transition-colors">
                  <Trash2 size={12} className="text-destructive/70" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CatFormRow({
  form, onChange, onSave, onCancel, isEdit,
}: {
  form: { name: string; icon: string; group: CategoryGroup; monthlyTarget: string };
  onChange: (f: { name: string; icon: string; group: CategoryGroup; monthlyTarget: string }) => void;
  onSave: () => void;
  onCancel: () => void;
  isEdit?: boolean;
}) {
  const { t } = useT();
  const set = (k: string, v: string) => onChange({ ...form, [k]: v } as typeof form);
  return (
    <form
      className="bg-muted/30 border-b border-border px-3 py-3"
      onKeyDown={onEnterToSubmitKeyDown}
      onSubmit={(e) => handleFormSubmit(e, onSave)}
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
        <div>
          <Label>{t("settings.icon")}</Label>
          <EmojiPicker
            value={form.icon}
            onChange={(emoji) => set("icon", emoji)}
            className="w-full justify-between"
            aria-label={t("settings.categoryIcon")}
          />
        </div>
        <div className="sm:col-span-2">
          <Label>{t("settings.name")}</Label>
          <input value={form.name} onChange={(e) => set("name", e.target.value)} className="app-input w-full" placeholder={t("settings.categoryName")} />
        </div>
        <div>
          <Label>{t("settings.monthlyTarget")}</Label>
          <input type="number" min="0" step="0.01" value={form.monthlyTarget} onChange={(e) => set("monthlyTarget", e.target.value)} className="app-input w-full" />
        </div>
        <div className="col-span-2 sm:col-span-4">
          <Label>{t("settings.group")}</Label>
          <select value={form.group} onChange={(e) => set("group", e.target.value as CategoryGroup)} className="app-input w-full">
            {GROUP_OPTIONS.map((g) => (
              <option key={g} value={g}>{groupLabel(g, t)}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={!form.name || !form.monthlyTarget} className="app-btn text-xs py-1.5 px-3">
          <Check size={12} />{isEdit ? t("common.save") : t("common.add")}
        </button>
        <button type="button" onClick={onCancel} className="app-btn-outline text-xs py-1.5 px-3">
          <X size={12} />{t("common.cancel")}
        </button>
      </div>
    </form>
  );
}

// ── Accounts section ──────────────────────────────────
function AccountsSection() {
  const { t } = useT();
  const accounts = useLiveQuery(() => db.accounts.toArray(), []) ?? [];
  const [editing, setEditing] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  type AccForm = { name: string; type: AccountType; initialBalance: string };
  const EMPTY_ACC: AccForm = { name: "", type: "checking", initialBalance: "" };
  const [accForm, setAccForm] = useState<AccForm>(EMPTY_ACC);

  const saveAccount = async (existing?: Account) => {
    if (!accForm.name) return;
    const payload: Account = {
      id:             existing?.id ?? uid(),
      name:           accForm.name,
      type:           accForm.type,
      initialBalance: parseFloat(accForm.initialBalance) || 0,
    };
    if (existing) {
      await db.accounts.put(payload);
      setEditing(null);
    } else {
      await db.accounts.add(payload);
      setAccForm(EMPTY_ACC);
      setAddOpen(false);
    }
  };

  const deleteAccount = async (id: string) => {
    if (!confirm(t("settings.deleteAccount"))) return;
    await db.accounts.delete(id);
  };

  const startEdit = (acc: Account) => {
    setEditing(acc.id);
    setAccForm({ name: acc.name, type: acc.type, initialBalance: String(acc.initialBalance) });
  };

  const resetAccForm = () => {
    setAddOpen(false);
    setEditing(null);
    setAccForm(EMPTY_ACC);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t("settings.accounts")}</h3>
        <button onClick={() => setAddOpen((v) => !v)} className="app-btn text-xs py-1.5 px-2.5">
          <Plus size={12} /> {t("common.add")}
        </button>
      </div>
      {addOpen && (
        <AccFormRow
          form={accForm}
          onChange={setAccForm}
          onSave={() => saveAccount()}
          onCancel={resetAccForm}
        />
      )}
      <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
        {accounts.length === 0 && (
          <p className="text-xs text-muted-foreground px-4 py-3">
            {t("settings.addFirstAccount")}
          </p>
        )}        {accounts.map((acc) => (
          <div key={acc.id}>
            {editing === acc.id ? (
              <AccFormRow
                form={accForm}
                onChange={setAccForm}
                onSave={() => saveAccount(acc)}
                onCancel={() => {
                  setEditing(null);
                  setAccForm(EMPTY_ACC);
                }}
                isEdit
              />
            ) : (
              <div className="flex items-center gap-3 px-3 py-2.5 bg-card hover:bg-muted/40 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{acc.name}</p>
                  <p className="text-[10px] text-muted-foreground capitalize">{accountTypeLabel(acc.type, t)}</p>
                </div>
                <p className={`text-xs tabular-nums font-mono ${acc.initialBalance < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                  {acc.initialBalance < 0 ? "-" : ""}${Math.abs(acc.initialBalance).toLocaleString()}
                </p>
                <button onClick={() => startEdit(acc)} className="p-1 rounded hover:bg-accent transition-colors">
                  <Pencil size={12} className="text-muted-foreground" />
                </button>
                <button onClick={() => deleteAccount(acc.id)} className="p-1 rounded hover:bg-destructive/10 transition-colors">
                  <Trash2 size={12} className="text-destructive/70" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Data management section ───────────────────────────
function DataSection() {
  const { t, locale: uiLocale } = useT();
  const { currency, format, locale } = useCurrency();
  const [seeding,   setSeeding]  = useState(false);
  const [clearing,  setClearing] = useState(false);
  const [confirm,   setConfirm]  = useState(false);
  const [done,      setDone]     = useState<string | null>(null);
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);
  const [verifyOk, setVerifyOk] = useState<boolean | null>(null);
  const [testReport, setTestReport] = useState<TestReport | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const handleSeed = async () => {
    setSeeding(true);
    setDone(null);
    try {
      await resetAndSeed(uiLocale);
      setDone(t("settings.demoLoaded"));
    } finally {
      setSeeding(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    setDone(null);
    try {
      await clearAllData(uiLocale);
      setConfirm(false);
      setDone(t("settings.clearedDone"));
    } finally {
      setClearing(false);
    }
  };

  const handleRunVerification = () => {
    const { ok, message } = runCustomerVerification();
    setVerifyOk(ok);
    setVerifyMessage(message);
    // Verbose assertion dump — compile-time gated so release builds drop this branch
    if (import.meta.env.DEV) {
      setTestReport(runBudgetCalculationTests());
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">{t("settings.dataMgmt")}</h3>

      {done && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 text-xs">
          <Check size={13} />
          {done}
        </div>
      )}

      {/* Backup: Export + Import */}
      <div className="app-card !p-4 space-y-2">
        <p className="text-sm font-semibold">{t("settings.backupTitle")}</p>
        <p className="text-xs text-muted-foreground">
          {t("settings.backupDesc")}
        </p>
        <BackupPanel showPrint={false} />
      </div>

      <div className="app-card !p-4 space-y-2">
        <p className="text-sm font-semibold">{t("settings.pdfTitle")}</p>
        <p className="text-xs text-muted-foreground">
          {t("settings.pdfDesc")}
        </p>
        <button
          type="button"
          disabled={pdfBusy}
          className="app-btn mt-1"
          onClick={() => {
            setPdfBusy(true);
            void exportAnnualReportFromDb({
              year: new Date().getFullYear(),
              currency,
              locale,
              uiLocale,
              formatCompact: (n) => format(n, true),
            }).finally(() => setPdfBusy(false));
          }}
        >
          <Download size={13} /> {pdfBusy ? t("dash.buildingPdf") : t("dash.downloadReport")}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="app-card !p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Database size={15} className="text-primary" />
            <p className="text-sm font-semibold">{t("settings.loadDemo")}</p>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("settings.loadDemoDesc")}
          </p>
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="app-btn mt-1"
          >
            <RotateCcw size={13} className={seeding ? "animate-spin" : ""} />
            {seeding ? t("settings.loadingDemo") : t("settings.loadDemoBtn")}
          </button>
        </div>

        <div className="app-card !p-4 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle size={15} className="text-destructive" />
            <p className="text-sm font-semibold text-destructive">{t("settings.clearAll")}</p>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("settings.clearAllDesc")}
          </p>
          {confirm ? (
            <div className="flex flex-wrap gap-2 mt-1">
              <button
                onClick={handleClear}
                disabled={clearing}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-destructive text-destructive-foreground text-xs font-semibold hover:opacity-90 transition-opacity"
              >
                <Trash2 size={12} />{clearing ? t("settings.clearing") : t("settings.clearConfirm")}
              </button>
              <button onClick={() => setConfirm(false)} className="app-btn-outline text-xs">
                {t("common.cancel")}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirm(true)}
              className="app-btn-outline mt-1 !border-destructive/40 !text-destructive hover:!bg-destructive/10"
            >
              <Trash2 size={13} />{t("settings.clearAll")}
            </button>
          )}
        </div>
      </div>

      {/* Customer-facing verification (one line). Verbose engine only in DEV / ?debug=1 */}
      <div className="app-card !p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <FlaskConical size={15} className="text-primary shrink-0" />
            <p className="text-sm font-semibold truncate">{t("settings.calcCheck")}</p>
          </div>
          <button onClick={handleRunVerification} className="app-btn-outline text-xs py-1.5 px-3 shrink-0">
            {t("settings.runVerification")}
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("settings.calcCheckDesc")}
        </p>

        {verifyMessage && (
          <p
            className={`text-sm font-medium ${
              verifyOk ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"
            }`}
          >
            {verifyMessage}
          </p>
        )}

        {import.meta.env.DEV && testReport && (
          <div className="mt-1 space-y-1.5 rounded-lg border border-dashed border-border bg-muted/30 p-3 text-xs">
            <div className="flex items-center justify-between font-semibold text-muted-foreground">
              <span>Dev test engine ({testReport.passedCount}/{testReport.total})</span>
              <span className="text-[10px]">hidden from customer builds</span>
            </div>
            <div className="divide-y divide-border/60 max-h-48 overflow-y-auto">
              {testReport.results
                .filter((r) => !r.success)
                .map((r, i) => (
                  <div key={i} className="py-1.5 text-[11px] text-destructive">
                    {r.name}: expected {String(r.expected)}, got {String(r.actual)}
                  </div>
                ))}
              {testReport.passed && (
                <p className="py-1.5 text-[11px] text-emerald-600 dark:text-emerald-400">All assertions passed.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Currency section ──────────────────────────────────
function CurrencySection() {
  const { t } = useT();
  const { currency, setCurrency, format } = useCurrency();
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">{t("settings.currencyLocale")}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
        <div>
          <Label>{t("settings.currency")}</Label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value as CurrencyCode)}
            className="app-input w-full"
          >
            {CURRENCY_OPTIONS.map((c) => (
              <option key={c.code} value={c.code}>{t(`currency.${c.code}`)}</option>
            ))}
          </select>
        </div>
        <div className="app-card !p-4 flex flex-col gap-1">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{t("settings.preview")}</p>
          <p className="text-2xl font-display text-foreground">{format(12345.67)}</p>
          <p className="text-xs text-muted-foreground">{t("settings.negative")} {format(-1234.5)}</p>
          <p className="text-xs text-muted-foreground">{t("settings.compact")} {format(123456, true)}</p>
        </div>
      </div>
    </div>
  );
}

// ── Collapsible section wrapper ───────────────────────
function Section({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <AppCard color="white" padding="md">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between mb-0 group"
      >
        <h2 className="text-base font-semibold">{title}</h2>
        {open
          ? <ChevronUp size={16} className="text-muted-foreground group-hover:text-foreground transition-colors" />
          : <ChevronDown size={16} className="text-muted-foreground group-hover:text-foreground transition-colors" />
        }
      </button>
      {open && <div className="mt-4">{children}</div>}
    </AppCard>
  );
}

// ── Main page ─────────────────────────────────────────
export function SettingsPage({
  onOpenHelp,
  onReplayTour,
}: {
  onOpenHelp?: () => void;
  onReplayTour?: () => void;
} = {}) {
  const { t, localeLocked } = useT();
  return (
    <div className="p-4 lg:p-6 space-y-4 w-full">
      <SectionTitle
        action={
          onOpenHelp ? (
            <button type="button" className="app-btn-outline text-xs min-h-[40px]" onClick={onOpenHelp}>
              {t("help.title")}
            </button>
          ) : undefined
        }
      >
        {t("settings.title")}
      </SectionTitle>
      <p className="text-xs text-muted-foreground -mt-2">{t("settings.subtitle")}</p>

      {/* Language picker: dev only — Etsy zips and hosted demo use a fixed locale */}
      {!localeLocked && (
        <Section title={t("settings.languageSection")}>
          <LanguageSection />
        </Section>
      )}

      <Section title={t("settings.currencyLocale")}>
        <CurrencySection />
      </Section>

      <Section title={t("settings.theme")}>
        <div className="space-y-3">
          <ThemePresets />
        </div>
      </Section>

      {(onReplayTour || onOpenHelp) && (
        <Section title={t("settings.helpSection")} defaultOpen={false}>
          <div className="flex flex-wrap gap-2">
            {onOpenHelp && (
              <button type="button" className="app-btn-outline min-h-[44px]" onClick={onOpenHelp}>
                {t("settings.openFaq")}
              </button>
            )}
            {onReplayTour && (
              <button type="button" className="app-btn-outline min-h-[44px]" onClick={onReplayTour}>
                {t("help.replayTour")}
              </button>
            )}
          </div>
        </Section>
      )}

      <Section title={t("settings.categories")} defaultOpen={false}>
        <CategoriesSection />
      </Section>

      <Section title={t("settings.accounts")} defaultOpen={false}>
        <AccountsSection />
      </Section>

      <Section title={t("settings.dataMgmt")} defaultOpen>
        <DataSection />
      </Section>

      <p className="text-center text-[10px] text-muted-foreground pt-2">
        {t("settings.footer")}
      </p>
    </div>
  );
}
